import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CookieBannerComponent } from './cookie-banner/cookie-banner.component';
import { AuthService, Profile } from 'providers/auth.service';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { OrganizationInfoService } from 'shared/organization-info/organization-info.service';
import { LicenseInfoService } from 'shared/organization-info/license-info.service';

/*
Service to handle interactions with Google Tag Manager / Google Analytics
*/

type WindowWithDataLayer = Window & {
    dataLayer: Record<string, string>[];
};
declare const window: WindowWithDataLayer;

@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
export class GoogleAnalyticsService {
    // observable for updating virtual page views
    private readonly _virtualViewStatus: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
    virtualViewStatus$ = this._virtualViewStatus.asObservable();
    virtualView = undefined;

    profile: Profile;
    domain = window.location.hostname;

    constructor(
        private auth: AuthService,
        private cookieBannerComponent: CookieBannerComponent,
        private organizationInfoService: OrganizationInfoService,
        private licenseService: LicenseInfoService
    ) {}

    // sends user property data
    sendAccessDataToGA(accessLevel: string, tenantId: string, subscriptionName: string, organizationName: string) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'login',
            access_level: accessLevel,
            tenant_id: tenantId,
            subscription_name: subscriptionName,
            organization_name: organizationName,
            app_origin: this.domain,
        });
    }

    // gets user account access level, tenant id, and license types
    async getDataForGA() {
        // No profile yet on /auth-callback or while redirecting to login: nothing to report.
        if (this.cookieBannerComponent.readConsentChoice() && this.auth.profile) {
            const tenantId = this.auth.profile.tenantId ? this.auth.profile.tenantId : 'Empty'; // impersonated organization's ID
            const organizationName = this.organizationInfoService.organizationName || 'Unknown';
            const activeSubscription = await firstValueFrom(
                this.licenseService.licenseInfo$.pipe(filter((license) => license !== null))
            ).catch(() => null);

            const subscriptionName = activeSubscription?.licenseDefinition?.name || 'Unknown';

            const topSystemRole = this.auth.getTopSystemRole();
            this.sendAccessDataToGA(topSystemRole, tenantId, subscriptionName, organizationName);
        }
    }

    // sends virtual page view event
    sendVirtualPageViewToGA(page_view: string) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'page_view',
            virtual_view: page_view,
            app_origin: this.domain,
        });
    }

    // updates status of virtual page view
    updateVirtualPageView(isMapMaximized: boolean, isShowWorldMap: boolean) {
        if (this.cookieBannerComponent.readConsentChoice()) {
            let newStatus: string;

            if (isMapMaximized && isShowWorldMap) {
                newStatus = 'world map';
            } else if (isMapMaximized && !isShowWorldMap) {
                newStatus = 'station grid';
            } else {
                newStatus = undefined;
            }
            this._virtualViewStatus.next(newStatus);
            this.sendVirtualPageViewToGA(newStatus);
        }
    }

    // sends global search event
    sendGlobalSearchToGA() {
        if (this.cookieBannerComponent.isLoginFired) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'global_search',
                app_origin: this.domain,
            });
        }
    }
}
